// The single MessageList, reused for operator DM / project room / person.
// Layout rules (from the design brief):
//   - flush-left, no bubbles, 22px avatar gutter, grouped runs
//   - an operator's messages get a 2px left accent spine + accent name,
//     rendered in the operator's one accent token
//   - your own line indents right, no spine
//   - the two-way agent exchange ("-> app -> agent" /
//     "<- agent -> app") renders in mono
//   - evidence renders in mono/faint
//   - operator interventions carry plain inline choices
//
// Near-greyscale at rest; the only colour is the operator accent on a
// load-bearing intervention.

import { useMemo, useState } from "react";
import { Volume2 } from "lucide-react";
import type { Message, OperatorId } from "@waytide/ui";
import { OPERATORS } from "@waytide/ui";
import { cn } from "./cn";
import { createListenController } from "@waytide/core";
import { webListen } from "../speech/webSpeech";

const GUTTER = "w-[22px] shrink-0";

function accentClassFor(operatorId?: OperatorId): string {
  if (!operatorId) return "text-primary";
  return OPERATORS[operatorId]?.accentClass ?? "text-primary";
}

function spineColorFor(operatorId?: OperatorId): string {
  // The 2px spine reuses the operator accent via currentColor on a
  // bordered element; we drive it with the same text-watch-* token.
  return accentClassFor(operatorId);
}

/** A single initial for the avatar gutter. Machine/system lines get a
 * mono glyph instead of a letter. */
function gutterGlyph(m: Message): string {
  switch (m.from) {
    case "you":
      return "";
    case "agent-in":
    case "agent-out":
      return "";
    case "evidence":
      return "";
    case "system":
      return "";
    default:
      return (m.author ?? "?").slice(0, 1).toUpperCase();
  }
}

function InlineChoices({ choices }: { choices: NonNullable<Message["choices"]> }) {
  return (
    <div className="mt-1 flex flex-wrap gap-2">
      {choices.map((c) => (
        <button
          key={c.label}
          type="button"
          // Mock-only: choices render but do not wire to a handler yet.
          className={cn(
            "rounded-sm border px-2 py-0.5 text-xs transition-colors",
            "border-edge text-muted hover:border-edge-strong hover:text-primary",
            c.emphasis && "text-primary",
          )}
        >
          [ {c.label} ]
        </button>
      ))}
    </div>
  );
}

function Row({
  m,
  listenState,
  onListen,
}: {
  m: Message;
  /** "hidden" (no engine / nothing to read) | "idle" | "playing". */
  listenState: "hidden" | "idle" | "playing";
  onListen: (m: Message) => void;
}) {
  const isYou = m.from === "you";
  const isOperator = m.from === "operator";
  const isAgent = m.from === "agent-in" || m.from === "agent-out";
  const isEvidence = m.from === "evidence";
  const isSystem = m.from === "system";
  const mono = isAgent || isEvidence;

  // The agent exchange carries its own mono direction prefix.
  const agentPrefix =
    m.from === "agent-in" ? "-> app -> agent" : m.from === "agent-out" ? "<- agent -> app" : "";

  return (
    <li
      className={cn(
        "wt-rise flex gap-2 px-3",
        m.sameAuthor ? "mt-0.5" : "mt-3 first:mt-0",
        // Your own line indents right, no spine.
        isYou && "pl-10",
      )}
    >
      {/* Avatar gutter — empty for grouped runs and for your own line. */}
      {!isYou && (
        <div className={cn(GUTTER, "pt-0.5 text-center")}>
          {!m.sameAuthor && gutterGlyph(m) && (
            <span
              className={cn(
                "inline-flex h-[18px] w-[18px] items-center justify-center rounded-full text-[0.625rem] font-medium",
                isOperator
                  ? cn("bg-surface", accentClassFor(m.operatorId))
                  : "bg-surface-raised text-muted",
              )}
            >
              {gutterGlyph(m)}
            </span>
          )}
        </div>
      )}

      <div
        className={cn(
          "min-w-0 flex-1",
          // The 2px accent spine on an operator's run.
          isOperator && cn("border-l-2 pl-2", spineColorFor(m.operatorId)),
        )}
        style={isOperator ? { borderColor: "currentColor" } : undefined}
      >
        {/* Name header (suppressed on grouped runs). */}
        {!m.sameAuthor && (m.author || isOperator) && (
          <div className="mb-0.5">
            <span
              className={cn(
                "text-xs font-medium",
                isOperator ? accentClassFor(m.operatorId) : "text-primary",
              )}
            >
              {m.author ?? OPERATORS[m.operatorId ?? "supervisor"]?.name}
            </span>
            {m.when && <span className="ml-2 text-[0.625rem] text-faint">{m.when}</span>}
          </div>
        )}

        {/* Body — with a LISTEN affordance (D7) when an engine exists. */}
        {m.text && (
          <div className="group flex items-start gap-1.5">
            <p
              className={cn(
                "min-w-0 flex-1 whitespace-pre-wrap break-words text-sm leading-snug",
                mono && "font-code text-[0.75rem] leading-relaxed",
                isEvidence && "text-faint",
                isSystem && "text-xs text-faint",
                isAgent && "text-muted",
                // Human lines (yours, operator, person) read at full primary.
                !mono && !isSystem && "text-primary",
              )}
            >
              {agentPrefix && (
                <span className="mr-2 select-none text-faint">{agentPrefix}</span>
              )}
              {m.text}
            </p>
            {listenState !== "hidden" && (
              <button
                type="button"
                onClick={() => onListen(m)}
                aria-label={listenState === "playing" ? "Stop reading" : "Read aloud"}
                aria-pressed={listenState === "playing"}
                data-testid="message-listen"
                className={cn(
                  "shrink-0 rounded-sm p-0.5 transition-colors",
                  listenState === "playing"
                    ? "text-primary"
                    : "text-faint opacity-0 hover:text-primary focus:opacity-100 group-hover:opacity-100",
                )}
              >
                <Volume2 size={12} />
              </button>
            )}
          </div>
        )}

        {m.choices && m.choices.length > 0 && <InlineChoices choices={m.choices} />}
      </div>
    </li>
  );
}

export function MessageList({ messages }: { messages: Message[] }) {
  // Listen (D7): one voice at a time across the whole list; toggling the
  // playing row stops it. The controller wraps the web TTS engine behind the
  // shared seam; no engine ⇒ every row's affordance is hidden.
  const listen = useMemo(() => createListenController(webListen), []);
  const [playing, setPlaying] = useState<string | null>(null);
  const available = listen.available();

  return (
    <ul className="flex flex-col py-3">
      {messages.map((m) => (
        <Row
          key={m.id}
          m={m}
          listenState={
            !available || !m.text
              ? "hidden"
              : playing === String(m.id)
                ? "playing"
                : "idle"
          }
          onListen={(msg) =>
            listen.toggle(String(msg.id), msg.text ?? "", setPlaying)
          }
        />
      ))}
    </ul>
  );
}
